import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {labels} from '../../../../../../../util/utils';
import {useCanvasFormMetadata} from '../../../../../../../util/customHooks';
import {entities, clientSideEventNames, itControlApproachType} from '../../../../../../../util/uiconstants';
import BodySpecialCardItem from '../BodySpecialCardItem';
import BodySpecialAccordionHeaderColumn from '../BodySpecialAccordionHeaderColumn';
import BodySpecialDeleteItemModal from '../BodySpecialDeleteItemModal';
import ISA315ManageITSPListItemExpanded from './ISA315ManageITSPListItemExpanded';
import {getAllItControls} from '../../../../../../../selectors/ISA315/selectors';
import {deleteItProcess} from '../../../../../../../actions/ITProcess/itprocessactions';
import EllipsesControl from '../../../../../EllipsesControl/EllipsesControl';

function ISA315ManageITSPListItem({
	itProcessId,
	onDropdownOptionClick,
	bodyId,
	engagementId,
	documentEngagementId,
	openRelateModal
}) {
	const dispatch = useDispatch();
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const itProcess = useSelector((state) => state.bodiesSpecial.itProcesses?.byId[itProcessId]);
	const itControls = useSelector(getAllItControls);

	const {isTargetEngagement} = useCanvasFormMetadata();

	const relatedITControls = useMemo(() => {
		return itControls?.filter((itControl) => itProcess.itControls?.includes(itControl.id));
	}, [itControls, itProcess]);

	function onEditItProcessClick() {
		onDropdownOptionClick({
			entityId: entities.ItProcess,
			entityUId: itProcessId,
			entityName: itProcess.itProcessName
		});
	}

	const dropdownOptions = useMemo(() => {
		return [
			{
				name: 'edit',
				render: isTargetEngagement ? (
					<EllipsesControl content={labels.viewItProcess} tooltip={labels.viewItProcess} noOfLines={1} />
				) : (
					<EllipsesControl content={labels.editItProcess} tooltip={labels.editItProcess} noOfLines={1} />
				),
				onClick: onEditItProcessClick
			},
			{
				name: 'delete',
				render: <EllipsesControl content={labels.deleteItProcess} tooltip={labels.deleteItProcess} noOfLines={1} />,
				onClick: () => setShowDeleteModal(!showDeleteModal)
			}
		];
	}, [isTargetEngagement]);

	const itRiskCount = itProcess.itRisks.length;

	const itGCCount = _.uniq(
		relatedITControls.filter((ic) => ic.itApproachTypeId === itControlApproachType.controls).map((ic) => ic.id)
	).length;

	const itSPCount = _.uniq(
		relatedITControls.filter((ic) => ic.itApproachTypeId === itControlApproachType.substantive).map((ic) => ic.id)
	).length;

	const itSPTestingCount = _.uniq(
		relatedITControls
			.filter((ic) => ic.itApproachTypeId === itControlApproachType.substantive && ic.hasITControlTesting === true)
			.map((ic) => ic.id)
	).length;

	// Columns render
	const config = useMemo(() => {
		return [
			{
				render: (
					<StyledItProcessColumn
						className={'ItProcessColumnHeader'}
						header={labels.iTProcess}
						value={itProcess.itProcessName}
					/>
				)
			},
			// Defining a column group without a header to draw the left border
			{
				className: 'primary-entity__child-entity ControlsGroup1',
				render: [
					{
						render: (
							<StyledISA315ManageITSPColumnCount
								className={'ITRiskCountHeader'}
								header={labels.itRiskColumnHeader}
								value={itRiskCount || '-'}
							/>
						)
					},
					{
						render: (
							<StyledISA315ManageITSPColumnCount
								className={'ITGCCountHeader'}
								header={labels.itGCColumnHeader}
								value={itGCCount || '-'}
							/>
						)
					}
				]
			},
			{
				className: 'primary-entity__child-entity ControlsGroup2',
				render: [
					{
						render: (
							<StyledISA315ManageITSPColumnCount
								className={'ITSPCountHeader'}
								header={labels.itSPColumnHeader}
								value={itSPCount || '-'}
							/>
						)
					},
					{
						render: (
							<StyledISA315ManageITSPColumnCount
								className={'TestCountHeader'}
								header={labels.itTestHeader}
								value={itSPTestingCount || '-'}
							/>
						)
					}
				]
			}
		];
	}, [itProcess, itSPTestingCount]);

	const hasRelatedItRisks = itProcess.itRisks && itProcess.itRisks.length > 0;

	return (
		<>
			<BodySpecialCardItem accordion config={config} dropdownOptions={dropdownOptions} disabled={!hasRelatedItRisks}>
				{hasRelatedItRisks && (
					<ISA315ManageITSPListItemExpanded
						itProcessId={itProcessId}
						bodyId={bodyId}
						documentEngagementId={documentEngagementId}
						engagementId={engagementId}
						onCreateUpdate={onDropdownOptionClick}
						openRelateModal={openRelateModal}
					/>
				)}
				{showDeleteModal && (
					<BodySpecialDeleteItemModal
						name={itProcess.itProcessName}
						title={labels.deleteItProcess}
						onDelete={() => {
							dispatch(deleteItProcess(engagementId, bodyId, itProcessId)).then(() =>
								window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations))
							);
						}}
						setIsLoading={() => {}}
						show={showDeleteModal}
						onHide={() => setShowDeleteModal(!showDeleteModal)}
					/>
				)}
			</BodySpecialCardItem>
		</>
	);
}

ISA315ManageITSPListItem.propTypes = {
	engagementId: PropTypes.number.isRequired,
	bodyId: PropTypes.number.isRequired,
	itProcessId: PropTypes.number.isRequired
};

export default ISA315ManageITSPListItem;

const StyledItProcessColumn = styled(BodySpecialAccordionHeaderColumn)`
	width: calc(100% - var(--px-400));
	padding: var(--px-20) var(--px-10) var(--px-20) 0;
	height: var(--px-87);
`;

const StyledItProcessControlsApplicationCount = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-120);
	padding: 0 var(--px-10) 0 0 !important;
`;

const StyledItProcessControlsItdmCount = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-80);
	padding: 0 0 0 var(--px-10) !important;
`;

const StyledISA315ManageITSPListItem = styled.section`
	.primary-entity__child-entity {
		width: var(--px-200);
		font-size: var(--px-12) !important;
		font-weight: 300 !important;
	}
`;

const StyledISA315ManageITSPColumnCount = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-100);
	padding: var(--px-24) var(--px-25);
`;
